import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { client } from '@/lib/api';

interface StaticPage {
  id: string;
  code: string;
  title: string;
  content: string;
  locale?: string;
  createdAt: string;
  updatedAt: string;
}

const PAGE_TYPES = [
  { code: 'terms', label: '利用規約', description: '出願時の同意事項' },
  {
    code: 'privacy',
    label: 'プライバシーポリシー',
    description: '個人情報の取り扱いについて',
  },
  {
    code: 'law',
    label: '特定商取引法に基づく表記',
    description: '法定表示事項',
  },
  {
    code: 'policy',
    label: '入学者受入方針',
    description: 'アドミッション・ポリシー',
  },
  {
    code: 'guide',
    label: '募集要項',
    description: '募集要項の本文またはリンク',
  },
  {
    code: 'contact',
    label: '問い合わせ窓口',
    description: '問い合わせ先の表示',
  },
];

// Partial definition for Hono client wrapper
type StaticPageClient = {
  api: {
    content: {
      'static-pages': {
        $get: () => Promise<Response>;
      };
    };
  };
};

export function StaticPageList() {
  const { data: pages, isLoading } = useQuery({
    queryKey: ['static-pages'],
    queryFn: async () => {
      const res = await (client as unknown as StaticPageClient).api.content['static-pages'].$get();
      return (await res.json()) as StaticPage[];
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const getPageStatus = (code: string) => {
    const page = pages?.find((p: StaticPage) => p.code === code);
    return page ? '設定済' : '未設定';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">静的コンテンツ設定</h1>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {PAGE_TYPES.map((type) => (
          <Card key={type.code} className="hover:bg-muted/50 transition-colors">
            <Link to={`/content/static/${type.code}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{type.label}</CardTitle>
                  <Badge variant={getPageStatus(type.code) === '設定済' ? 'secondary' : 'outline'}>
                    {getPageStatus(type.code)}
                  </Badge>
                </div>
                <CardDescription>{type.description}</CardDescription>
              </CardHeader>
            </Link>
          </Card>
        ))}
      </div>
    </div>
  );
}
